<script>
    import { createEventDispatcher, onMount } from "svelte";
    import { sanitizeHtml } from "../../sanitize.js";

    export let options = [];
    export let matches = {}; // { optionServerId: matchServerId }

    const dispatch = createEventDispatcher();

    let selectingFor = null; // optionServerId currently being selected
    let matchChoices = [];

    // Fonction de mélange (Fisher-Yates shuffle)
    function shuffle(array) {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }

    // Initialiser et mélanger les choix au montage
    onMount(() => {
        matchChoices = shuffle(options.map(opt => ({
            serverId: opt.serverId,
            text: opt.optionTextMatch
        })));
    });

    // Mettre à jour si les options changent (sans re-mélanger si déjà initialisé)
    $: if (matchChoices.length === 0 && options.length > 0) {
        matchChoices = shuffle(options.map(opt => ({
            serverId: opt.serverId,
            text: opt.optionTextMatch
        })));
    }

    // Reactive map of optionServerId -> matchText
    $: matchTexts = Object.fromEntries(
        Object.entries(matches).map(([optionId, matchId]) => {
            const match = matchChoices.find(m => m.serverId === matchId);
            return [optionId, match?.text || null];
        })
    );

    function startSelecting(optionServerId) {
        selectingFor = optionServerId;
    }

    function cancelSelecting() {
        selectingFor = null;
    }

    function selectMatch(matchServerId) {
        if (selectingFor === null) return;

        // Si le match est déjà utilisé par une autre option, le retirer d'abord
        const currentOwner = getOptionUsingMatch(matchServerId);
        if (currentOwner && currentOwner !== selectingFor) {
            dispatch("match", {
                optionId: currentOwner,
                matchId: null
            });
        }

        dispatch("match", {
            optionId: selectingFor,
            matchId: matchServerId
        });
        selectingFor = null;
    }

    function clearMatch(optionServerId) {
        dispatch("match", {
            optionId: optionServerId,
            matchId: null
        });
    }

    function isMatchUsed(matchServerId) {
        return Object.values(matches).includes(matchServerId);
    }

    function getOptionUsingMatch(matchServerId) {
        return Object.entries(matches).find(([_, v]) => v === matchServerId)?.[0];
    }
</script>

<div class="match-container">
    <!-- Propositions avec leurs emplacements de match -->
    <div class="propositions-list">
        {#each options as option, index}
            {@const currentMatch = matchTexts[option.serverId]}
            {@const isSelecting = selectingFor === option.serverId}

            <div class="proposition-card bg-base-100 {isSelecting ? 'border-primary ring-2 ring-primary' : 'border-primary/30'}">
                <div class="proposition-top">
                    <span class="proposition-text text-base-content">{@html sanitizeHtml(option.optionText)}</span>
                </div>

                <div class="proposition-divider bg-primary/20"></div>

                <div class="proposition-bottom {isSelecting ? 'bg-primary/10' : ''}">
                    {#if currentMatch}
                        <div class="match-selected">
                            <span class="match-text text-base-content bg-primary/15 border-2 border-primary rounded-lg px-4 py-2">{@html sanitizeHtml(currentMatch)}</span>
                            <button
                                type="button"
                                class="btn btn-ghost btn-xs"
                                on:click={() => startSelecting(option.serverId)}
                                title="Changer"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                                </svg>
                            </button>
                        </div>
                    {:else}
                        <button
                            type="button"
                            class="btn btn-outline btn-primary btn-sm choose-btn {isSelecting ? 'btn-active' : ''}"
                            on:click={() => isSelecting ? cancelSelecting() : startSelecting(option.serverId)}
                        >
                            {isSelecting ? 'Annuler' : 'Choisir'}
                        </button>
                    {/if}
                </div>
            </div>

            <!-- Panneau des matchs affiché juste après la proposition en cours de sélection -->
            {#if isSelecting}
                <div class="matches-panel bg-base-200 border-primary/30">
                    <div class="matches-header">
                        <span class="text-base-content font-medium">Sélectionnez une correspondance</span>
                    </div>
                    <div class="matches-list">
                        {#each matchChoices as match}
                            {@const isUsed = isMatchUsed(match.serverId)}
                            {@const isCurrentSelection = matches[selectingFor] === match.serverId}

                            <button
                                type="button"
                                class="match-choice bg-base-100 hover:border-primary hover:bg-primary/10
                                       {isCurrentSelection ? 'border-primary bg-primary/20' : 'border-primary/30'}
                                       {isUsed && !isCurrentSelection ? 'opacity-60' : ''}"
                                on:click={() => selectMatch(match.serverId)}
                            >
                                <span class="match-choice-text text-base-content">{@html sanitizeHtml(match.text)}</span>
                                {#if isUsed && !isCurrentSelection}
                                    <span class="badge badge-sm badge-neutral">Utilisé</span>
                                {/if}
                            </button>
                        {/each}
                    </div>
                </div>
            {/if}
        {/each}
    </div>

    <!-- Panneau des matchs affiché en bas quand aucune sélection n'est en cours -->
    {#if selectingFor === null}
        <div class="matches-panel bg-base-200 border-primary/30">
            <div class="matches-header">
                <span class="text-base-content font-medium">Sélectionnez une correspondance</span>
            </div>
            <div class="matches-list">
                {#each matchChoices as match}
                    {@const isUsed = isMatchUsed(match.serverId)}

                    <button
                        type="button"
                        class="match-choice bg-base-100 border-primary/30
                               {isUsed ? 'opacity-60' : ''}"
                        disabled
                    >
                        <span class="match-choice-text text-base-content">{@html sanitizeHtml(match.text)}</span>
                        {#if isUsed}
                            <span class="badge badge-sm badge-neutral">Utilisé</span>
                        {/if}
                    </button>
                {/each}
            </div>
        </div>
    {/if}
</div>

<style>
    .match-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .propositions-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .proposition-card {
        border: 2px solid;
        border-radius: 12px;
        overflow: hidden;
        transition: all 0.2s;
    }

    .proposition-top {
        padding: 1rem 1.5rem;
    }

    .proposition-text {
        font-size: 1rem;
        line-height: 1.5;
        font-weight: 500;
    }

    .proposition-divider {
        height: 2px;
    }

    .proposition-bottom {
        padding: 1rem 1.5rem;
        min-height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.2s;
    }

    .match-selected {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }

    .match-selected button {
        position: absolute;
        right: 0;
    }

    .match-text {
        font-size: 0.95rem;
        line-height: 1.5;
    }

    .choose-btn {
        min-width: 100px;
    }

    .matches-panel {
        border: 2px solid;
        border-radius: 12px;
        padding: 1rem;
    }

    .matches-header {
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid oklch(var(--bc) / 0.1);
    }

    .matches-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .match-choice {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        border: 2px solid;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        text-align: left;
        width: 100%;
    }

    .match-choice-text {
        flex: 1;
        font-size: 0.95rem;
        line-height: 1.4;
    }
</style>
